import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Vollbild-Loading-Overlay für lange API-Calls (Perplexity, Claude, Calendar).
 *
 * Props:
 *   title: kurzer Titel (z.B. "Perplexity recherchiert das Web")
 *   subtitle: längere Beschreibung
 *   steps: optional Array von Strings — werden mit Verzögerung als "✓ Schritt X" eingeblendet,
 *     erzeugt den Eindruck dass etwas passiert. Auch wenn der API-Call eine Black-Box ist.
 *   estimatedSeconds: für Zeit-Indikator (z.B. 20)
 *   onCancel: optional Cancel-Callback
 */
export default function LongOperationOverlay({ title, subtitle, steps = [], estimatedSeconds = 20, onCancel }) {
  const [elapsed, setElapsed] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const tick = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 250);
    return () => clearInterval(tick);
  }, []);

  // Schritte alle ~estimatedSeconds/N einblenden
  useEffect(() => {
    if (!steps.length) return;
    const interval = Math.max(1000, (estimatedSeconds * 1000) / (steps.length + 1));
    const timer = setInterval(() => {
      setStepIndex((i) => Math.min(i + 1, steps.length));
    }, interval);
    return () => clearInterval(timer);
  }, [steps, estimatedSeconds]);

  const progressPct = Math.min(100, (elapsed / estimatedSeconds) * 100);

  return (
    <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="bg-white max-w-[460px] w-full p-6 md:p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-3">
          <Loader2 size={28} className="animate-spin text-[#658a07] flex-shrink-0" />
          <h3 className="text-lg font-bold uppercase text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {title}
          </h3>
        </div>

        {subtitle && (
          <p className="text-sm text-gray-600 mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {subtitle}
          </p>
        )}

        {/* Progress-Bar */}
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
          <div className="h-full bg-[#befa0a] transition-all duration-300 ease-out" style={{ width: `${progressPct}%` }} />
        </div>

        <p className="text-[11px] text-gray-400 mb-4">
          {elapsed}s vergangen{estimatedSeconds ? ` · typisch ${estimatedSeconds}s` : ''}
        </p>

        {/* Schritte */}
        {steps.length > 0 && (
          <ul className="space-y-1.5 mb-4">
            {steps.map((s, i) => (
              <li key={i} className={`text-xs flex items-center gap-2 transition-opacity duration-500 ${
                i < stepIndex ? 'opacity-100' : i === stepIndex ? 'opacity-100' : 'opacity-30'
              }`}>
                {i < stepIndex ? (
                  <span className="text-[#658a07] font-bold">✓</span>
                ) : i === stepIndex ? (
                  <Loader2 size={11} className="animate-spin text-[#658a07]" />
                ) : (
                  <span className="text-gray-300">○</span>
                )}
                <span className={i < stepIndex ? 'text-gray-700' : i === stepIndex ? 'text-[#1f1f1f] font-bold' : 'text-gray-400'}>
                  {s}
                </span>
              </li>
            ))}
          </ul>
        )}

        {onCancel && elapsed > 5 && (
          <button onClick={onCancel}
            className="w-full text-[11px] uppercase tracking-wider text-gray-500 hover:text-[#1f1f1f] py-1.5 border border-gray-200 mt-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            Abbrechen
          </button>
        )}
      </div>
    </div>
  );
}
